import { IconName, LPSettlementInstructionStatusEnum, SettlementInstructionDirectionEnum } from '@talos/kyoko';
import type { DefaultTheme } from 'styled-components';

/**
 * Asset status mapping with icons and descriptions
 * Based on the asset status legend - excluding Marked statuses, Late, and Rejected
 */
export const LP_ASSET_STATUS_MAPPING = {
  // Pending Initiation - Outgoing not yet initiated
  'Pending Initiation': {
    icon: IconName.Clock,
    getColor: (theme: DefaultTheme) => theme.colors.gray['090'],
    description: 'Outgoing not yet initiated',
  },
  // Pending Receipt - Incoming not yet received
  'Pending Receipt': {
    icon: IconName.Clock,
    getColor: (theme: DefaultTheme) => theme.colors.gray['090'],
    description: 'Incoming not yet received',
  },
  // In Flight - Outgoing transfer initiated but not yet completed
  'In Flight': {
    icon: IconName.PaperAirplane,
    getColor: (theme: DefaultTheme) => theme.colors.blue.default,
    description: 'Outgoing transfer initiated but not yet completed',
  },
  // Received - Incoming asset received and settled
  Received: {
    icon: IconName.CheckCircle,
    getColor: (theme: DefaultTheme) => theme.colors.green.default,
    description: 'Incoming asset received and settled (system matched transfer or manually marked)',
  },
  // Sent - Outgoing asset sent and settled
  Sent: {
    icon: IconName.CheckCircle,
    getColor: (theme: DefaultTheme) => theme.colors.green.default,
    description: 'Outgoing asset sent and settled',
  },
  Error: {
    icon: IconName.CloseCircle,
    getColor: (theme: DefaultTheme) => theme.colors.red.default,
    description: 'Error',
  },
} as const;

export type LPAssetStatus = keyof typeof LP_ASSET_STATUS_MAPPING;

export type LPAssetStatusMapping = {
  icon: IconName;
  getColor: (theme: DefaultTheme) => string;
  description: string;
};

export type LPAssetStatusMappingWithColor = {
  icon: IconName;
  color: string;
  description: string;
};

/**
 * Determine the detailed asset status based on enum status and direction
 */
export function getDetailedLPAssetStatus(
  status: LPSettlementInstructionStatusEnum,
  direction: SettlementInstructionDirectionEnum
): LPAssetStatus | null {
  switch (status) {
    case LPSettlementInstructionStatusEnum.Pending:
      return direction === SettlementInstructionDirectionEnum.Outbound ? 'Pending Initiation' : 'Pending Receipt';

    case LPSettlementInstructionStatusEnum.Completed:
      return direction === SettlementInstructionDirectionEnum.Outbound ? 'Sent' : 'Received';

    case LPSettlementInstructionStatusEnum.InFlight:
      return 'In Flight';

    case LPSettlementInstructionStatusEnum.Error:
      return 'Error';

    case LPSettlementInstructionStatusEnum.Previewed:
    default:
      // For Error, Previewed, or unknown statuses, return null (no mapping)
      return null;
  }
}

/**
 * Get status mapping for a given status
 */
export function getLPAssetStatusMapping(
  status: LPAssetStatus,
  theme: DefaultTheme
): LPAssetStatusMappingWithColor | null {
  const mapping = LP_ASSET_STATUS_MAPPING[status];
  if (mapping) {
    return {
      icon: mapping.icon,
      color: mapping.getColor(theme),
      description: mapping.description,
    };
  }
  return null;
}

/**
 * Check if a status is a valid asset status
 */
export function isValidLPAssetStatus(status: string): status is LPAssetStatus {
  return status in LP_ASSET_STATUS_MAPPING;
}
